import React, { useState } from 'react';
import styled from 'styled-components';
import { MdEmail, MdPhone, MdCall, MdLocationOn } from 'react-icons/md';

const FooterContainer = styled.footer`
  background: 
    radial-gradient(circle at 25% 20%, rgba(212, 175, 55, 0.015) 0%, transparent 50%),
    radial-gradient(circle at 75% 80%, rgba(212, 175, 55, 0.012) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.006) 0%, transparent 60%),
    linear-gradient(135deg, var(--color-obsidian) 0%, var(--color-charcoal) 20%, var(--color-obsidian) 40%, var(--color-charcoal) 60%, var(--color-obsidian) 80%, var(--color-charcoal) 100%);
  border-top: 2px solid rgba(212, 175, 55, 0.25);
  padding: 5rem 2rem 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 4px 8px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 25%, rgba(212, 175, 55, 0.02) 0%, transparent 60%),
      radial-gradient(circle at 85% 75%, rgba(212, 175, 55, 0.015) 0%, transparent 60%),
      radial-gradient(circle at 50% 10%, rgba(212, 175, 55, 0.008) 0%, transparent 70%),
      radial-gradient(circle at 30% 90%, rgba(212, 175, 55, 0.006) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    animation: luxury-ambient-glow 40s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.4), var(--color-champagne), var(--color-rose-gold), var(--color-champagne), rgba(212, 175, 55, 0.4), transparent);
    opacity: 0.9;
    animation: luxury-divider-pulse 8s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    padding: 4rem 1.5rem 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1rem 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 2.5rem 0.75rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
    margin-bottom: 2rem;
    max-width: 800px;
  }
  
  @media (max-width: 768px) {
    gap: 2rem;
    margin-bottom: 1.75rem;
    max-width: 600px;
  }
  
  @media (max-width: 576px) {
    gap: 1.75rem;
    margin-bottom: 1.5rem;
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
    margin-bottom: 1.25rem;
    max-width: 350px;
  }
  
  @media (max-width: 375px) {
    gap: 1.25rem;
    margin-bottom: 1rem;
    max-width: 320px;
    padding: 0 1rem;
  }
  
  @media (max-width: 320px) {
    gap: 1rem;
    margin-bottom: 0.75rem;
    max-width: 290px;
    padding: 0 0.5rem;
  }
`;

const CompanySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 968px) {
    align-items: center;
    text-align: center;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Logo = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
  color: var(--color-obsidian);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: 800;
  font-family: 'Inter', sans-serif;
  border: 1px solid rgba(212, 175, 55, 0.3);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.3), transparent);
    border-radius: 12px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.6s ease;
  }
  
  &:hover {
    background: var(--color-obsidian);
    color: var(--color-champagne);
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 16px 40px rgba(212, 175, 55, 0.25);
    
    &::before {
      opacity: 1;
    }
  }
`;

const CompanyName = styled.h3`
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
  transition: all 0.4s ease;
  text-shadow: 0 1px 2px rgba(212, 175, 55, 0.1);
  
  &:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }
`;

const Tagline = styled.p`
  color: var(--color-slate-300);
  font-size: 1rem;
  margin: 0.5rem 0 3rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  opacity: 0.9;
`;

const CTATitle = styled.h4`
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  margin-bottom: 1rem;
  letter-spacing: -0.03em;
  line-height: 1.1;
  position: relative;
  
  background: linear-gradient(135deg, #D4AF37 0%, #E8B86D 25%, #F8F8F8 50%, #E8B86D 75%, #D4AF37 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: luxury-shimmer 4s ease-in-out infinite;
  
  text-shadow: 
    0 2px 4px rgba(11, 11, 11, 0.9),
    0 0 30px rgba(212, 175, 55, 0.08);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, var(--color-champagne), var(--color-rose-gold), var(--color-champagne));
    opacity: 0.7;
    border-radius: 2px;
  }
  
  @keyframes luxury-shimmer {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

const CTADescription = styled.p`
  color: var(--color-slate-300);
  line-height: 1.5;
  margin-bottom: 1.5rem;
  max-width: 400px;
  font-weight: 400;
  font-size: 0.95rem;
  opacity: 0.95;
  letter-spacing: 0.01em;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.5);
`;

const ContactInfo = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -1rem;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, var(--color-champagne), var(--color-rose-gold));
    opacity: 0.6;
    border-radius: 1px;
  }
`;

const ContactItem = styled.p`
  color: var(--color-slate-300);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 500;
  font-size: 1.05rem;
  padding: 0.75rem 0;
  border-radius: 8px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0%;
    background: linear-gradient(135deg, var(--color-champagne), var(--color-rose-gold));
    border-radius: 2px;
    transition: height 0.4s ease;
  }
  
  &:hover {
    color: var(--color-pearl);
    transform: translateX(8px);
    
    &::before {
      height: 60%;
    }
  }
  
  .contact-icon {
    font-size: 1.2rem;
    color: var(--color-champagne);
    filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.1));
    transition: all 0.4s ease;
  }
  
  &:hover .contact-icon {
    color: var(--color-rose-gold);
    transform: scale(1.1);
  }
  
  @media (max-width: 968px) {
    justify-content: center;
    transform: none;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const ContactForm = styled.form`
  background: rgba(212, 175, 55, 0.025);
  border: 1px solid rgba(212, 175, 55, 0.12);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  position: relative;
  backdrop-filter: blur(20px);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.015) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.08), transparent, rgba(212, 175, 55, 0.04), transparent);
    border-radius: 24px;
    z-index: -1;
  }
`;

const FormTitle = styled.h4`
  color: var(--color-pearl);
  font-size: 2rem;
  margin-bottom: 3rem;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.2;
  position: relative;
  z-index: 2;
  
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 50%, var(--color-champagne) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.8);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--color-champagne), transparent);
    opacity: 0.6;
    border-radius: 1px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  background: rgba(212, 175, 55, 0.02);
  border: 1px solid rgba(212, 175, 55, 0.1);
  border-radius: 8px;
  color: var(--color-pearl);
  font-size: 0.95rem;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(16px);
  font-weight: 400;
  letter-spacing: 0.01em;
  position: relative;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.3);
    box-shadow: 
      0 0 0 2px rgba(212, 175, 55, 0.08),
      0 8px 20px rgba(212, 175, 55, 0.05);
    background: rgba(212, 175, 55, 0.04);
    transform: translateY(-3px);
  }
  
  &::placeholder {
    color: var(--color-slate-400);
    font-weight: 300;
    opacity: 0.8;
  }
  
  &:hover {
    border-color: rgba(212, 175, 55, 0.2);
    background: rgba(212, 175, 55, 0.03);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.25rem;
  background: rgba(212, 175, 55, 0.02);
  border: 1px solid rgba(212, 175, 55, 0.1);
  border-radius: 8px;
  color: var(--color-pearl);
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;
  font-family: 'Inter', sans-serif;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(16px);
  font-weight: 400;
  letter-spacing: 0.01em;
  line-height: 1.5;
  
  &:focus {
    outline: none;
    border-color: rgba(212, 175, 55, 0.3);
    box-shadow: 
      0 0 0 2px rgba(212, 175, 55, 0.08),
      0 8px 20px rgba(212, 175, 55, 0.05);
    background: rgba(212, 175, 55, 0.04);
    transform: translateY(-3px);
  }
  
  &::placeholder {
    color: var(--color-slate-400);
    font-weight: 300;
    opacity: 0.8;
  }
  
  &:hover {
    border-color: rgba(212, 175, 55, 0.2);
    background: rgba(212, 175, 55, 0.03);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: var(--color-champagne);
  color: var(--color-obsidian);
  padding: 1rem 1.5rem;
  border: 1px solid var(--color-champagne);
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(16px);
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(254, 254, 254, 0.25), transparent);
    transition: left 0.8s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0;
    height: 0;
    background: rgba(11, 11, 11, 0.1);
    border-radius: 50%;
    transition: all 0.6s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover::after {
    width: 300px;
    height: 300px;
  }
  
  &:hover {
    background: var(--color-obsidian);
    color: var(--color-champagne);
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 20px 40px rgba(212, 175, 55, 0.3);
    border-color: var(--color-champagne);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      background: var(--color-champagne);
      color: var(--color-obsidian);
      box-shadow: 0 8px 20px rgba(212, 175, 55, 0.15);
      transform: none;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(212, 175, 55, 0.12);
  padding-top: 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--color-champagne), var(--color-rose-gold), var(--color-champagne), transparent);
    opacity: 0.4;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.1), transparent);
  }
`;

const Credit = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--color-slate-400);
  font-weight: 500;
  letter-spacing: 0.03em;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.5);
  
  span {
    background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 600;
  }
`;

const Footer: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Contact form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  return (
    <FooterContainer>
      <FooterContent>
        <CompanySection>          
          <LogoContainer>
            <Logo>K</Logo>
            <CompanyName>KIII TOYS STUDIOS</CompanyName>
          </LogoContainer>
          <Tagline>Masterpieces in Miniature</Tagline>
          
          <CTATitle>Bring Heroes Home</CTATitle>
          <CTADescription>
            Transform your favorite characters into stunning collectible masterpieces. Every hero deserves 
            a place in your collection, crafted with legendary attention to detail and unmatched artistry.
          </CTADescription>
          
          <ContactInfo>
            <ContactItem>{React.createElement(MdEmail as React.ComponentType<any>, { className: "contact-icon" })} info@k3toysstudios.com</ContactItem>
            <ContactItem>{React.createElement(MdPhone as React.ComponentType<any>, { className: "contact-icon" })} +91 95534 88646</ContactItem>
            <ContactItem>{React.createElement(MdCall as React.ComponentType<any>, { className: "contact-icon" })} +91 90147 46666</ContactItem>
            <ContactItem>{React.createElement(MdLocationOn as React.ComponentType<any>, { className: "contact-icon" })} Hyderabad, Telangana</ContactItem>
          </ContactInfo>
        </CompanySection>
        
        <ContactForm onSubmit={handleSubmit}>
          <FormTitle>Start Your Legacy</FormTitle>
          
          <FormRow>
            <Input
              type="text"
              name="fullName"
              placeholder="Full Name *"
              value={formData.fullName}
              onChange={handleInputChange}
              required
            />
            <Input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </FormRow>
          
          <FormRow>
            <Input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <Input
              type="text"
              name="subject"
              placeholder="Subject *"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </FormRow>
          
          <FormGroup>
            <TextArea
              name="message"
              placeholder="Tell us about your project, ideas, or any questions you have..."
              value={formData.message}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </SubmitButton>
        </ContactForm>
      </FooterContent>
      
      <FooterBottom>
        <Credit>© 2025 <span>KIII Toys Studios</span>. All rights reserved. | Powered by <span>DreamMenders</span></Credit>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;